import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/base/EventCard";
import { useSeo } from "@/hooks/useSeo";
import { events, eventCategories, eventStatusList } from "@/mocks/events";

const SEO_TITLE = "事件追踪 - 网报吃瓜通报、声明、辟谣与调查进展列表";
const SEO_DESC =
  "网报吃瓜事件追踪列表，可按关键词、主题分类与处理状态筛选官方通报、声明、回应、辟谣与调查进展，查看每件事的完整时间线还原。";
const SEO_KEYWORDS = "网报吃瓜通报,网报吃瓜声明,网报吃瓜辟谣,网报吃瓜调查,事件时间线,官方回应";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "CollectionPage",
      name: SEO_TITLE,
      description: SEO_DESC,
      url: "https://sxdayun.com.cn/events",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://sxdayun.com.cn/" },
        { "@type": "ListItem", position: 2, name: "事件追踪", item: "https://sxdayun.com.cn/events" },
      ],
    },
    {
      "@type": "ItemList",
      name: "网报吃瓜事件追踪列表",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "云溪猫耳山景区天价餐饮争议",
          url: "https://sxdayun.com.cn/events/cat-ear-mountain-price",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "云溪将停水三天传言辟谣",
          url: "https://sxdayun.com.cn/events/water-outage-rumor",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "网红零食代糖超标说法",
          url: "https://sxdayun.com.cn/events/sugar-free-snack",
        },
      ],
    },
  ],
};

export default function Events() {
  const [searchParams] = useSearchParams();
  const urlQ = searchParams.get("q") ?? "";
  const urlCategory = searchParams.get("category") ?? "全部";

  const [query, setQuery] = useState(urlQ);
  const [keywordDraft, setKeywordDraft] = useState(urlQ);
  const [category, setCategory] = useState(urlCategory);
  const [status, setStatus] = useState("全部");

  useEffect(() => {
    setQuery(urlQ);
    setKeywordDraft(urlQ);
  }, [urlQ]);

  useEffect(() => {
    setCategory(eventCategories.includes(urlCategory) ? urlCategory : "全部");
  }, [urlCategory]);

  const filtered = useMemo(() => {
    const k = query.trim().toLowerCase();
    return events.filter((event) => {
      const matchCategory = category === "全部" || event.category === category;
      const matchStatus = status === "全部" || event.status === status;
      const matchKeyword =
        !k ||
        event.title.toLowerCase().includes(k) ||
        event.summary.toLowerCase().includes(k) ||
        event.category.toLowerCase().includes(k) ||
        event.tags.some((tag) => tag.toLowerCase().includes(k));
      return matchCategory && matchStatus && matchKeyword;
    });
  }, [query, category, status]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setQuery(keywordDraft);
  };

  const resetAll = () => {
    setQuery("");
    setKeywordDraft("");
    setCategory("全部");
    setStatus("全部");
  };

  useSeo({
    title: SEO_TITLE,
    description: SEO_DESC,
    keywords: SEO_KEYWORDS,
    path: "/events",
    jsonLd: JSON_LD,
  });

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100/60">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "事件追踪" }]} />
            <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
              事件追踪
            </h1>
            <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              按主题与处理状态筛选网报吃瓜收录的官方通报、声明、回应与辟谣，点开任意事件即可查看按时间排列的完整还原过程。
            </p>

            <form
              onSubmit={handleSearch}
              className="mt-7 flex w-full max-w-2xl flex-col gap-3 sm:flex-row"
            >
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="search"
                  value={keywordDraft}
                  onChange={(e) => setKeywordDraft(e.target.value)}
                  placeholder="搜索事件标题、标签或发布主体"
                  aria-label="搜索事件标题、标签或发布主体"
                  className="h-11 w-full rounded-md border border-background-300 bg-background-50 pl-10 pr-4 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-300/60"
                />
              </div>
              <button
                type="submit"
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-search-line"></i>
                </span>
                搜索
              </button>
              <button
                type="button"
                onClick={resetAll}
                className="inline-flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-refresh-line"></i>
                </span>
                重置
              </button>
            </form>
          </div>
        </section>

        <section className="w-full">
          <div className="mx-auto w-full max-w-[1200px] px-4 py-8 md:px-8 md:py-12">
            <div className="flex flex-col gap-5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium text-foreground-500">主题</span>
                {eventCategories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                      category === cat
                        ? "border-primary-500 bg-primary-500 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium text-foreground-500">状态</span>
                {eventStatusList.map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setStatus(st)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
                      status === st
                        ? "border-secondary-600 bg-secondary-600 text-background-50"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-400"
                    }`}
                  >
                    {st}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center justify-between border-t border-background-200 pt-6">
              <p className="text-sm text-foreground-600">
                共找到 <span className="font-semibold text-foreground-950">{filtered.length}</span> 件事件
                {query && (
                  <>
                    ，关键词「<span className="text-accent-600">{query}</span>」
                  </>
                )}
              </p>
            </div>

            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {filtered.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                  没有匹配的事件
                </h2>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                  换个关键词，或清除筛选条件再试一次。如果这件事官方已经发声但这里还没收录，欢迎在联系纠错页提交线索。
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-refresh-line"></i>
                  </span>
                  清除筛选
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}